import { createEffect, createSignal, onCleanup, onMount } from 'solid-js'
import { revealScrollLeft } from './revealScrollLeft'

const TRAVEL_MS = 460
const LENS_GAIN = 0.04
const LENS_REACH = 0.72
const LENS_EPSILON = 1.004

export type TabAxis = 'x' | 'y'

export interface TabPill {
  x: number
  y: number
  w: number
  h: number
}

export function createTabIndicator(active: () => string, axis: () => TabAxis = () => 'x') {
  let listEl: HTMLElement | undefined
  let indicatorEl: HTMLElement | undefined
  const triggers = new Map<string, HTMLElement>()
  const [pill, setPill] = createSignal<TabPill | null>(null)
  const [glide, setGlide] = createSignal(false)
  const [direction, setDirection] = createSignal<'left' | 'right' | null>(null)
  const [moving, setMoving] = createSignal(false)
  const reduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  let lastOffset: number | null = null
  let lastAxis: TabAxis | null = null
  let settleTimer: ReturnType<typeof setTimeout> | undefined
  let lensRaf = 0
  let resizeObserver: ResizeObserver | undefined

  const activeCenter = () => {
    const el = triggers.get(active())
    if (!el) {
      return null
    }
    return centerOf(el.getBoundingClientRect())
  }

  const centerOf = (rect: DOMRect) =>
    axis() === 'y' ? rect.top + rect.height / 2 : rect.left + rect.width / 2
  const lengthOf = (rect: DOMRect) => (axis() === 'y' ? rect.height : rect.width)

  const lensFrame = (centerOverride?: number | null) => {
    if (reduced) {
      return
    }
    let center = centerOverride
    if (center == null) {
      if (!indicatorEl) {
        return
      }
      center = centerOf(indicatorEl.getBoundingClientRect())
    }
    for (const el of triggers.values()) {
      const content = el.querySelector<HTMLElement>('.ds-tab-content')
      if (!content) {
        continue
      }
      const rect = el.getBoundingClientRect()
      const d = (center - centerOf(rect)) / (lengthOf(rect) * LENS_REACH)
      const s = 1 + LENS_GAIN * Math.exp(-d * d)
      content.style.transform = s > LENS_EPSILON ? `scale(${s.toFixed(4)})` : ''
    }
  }

  const runLens = () => {
    cancelAnimationFrame(lensRaf)
    const t0 = performance.now()
    const tick = (t: number) => {
      lensFrame()
      if (t - t0 < TRAVEL_MS + 100) {
        lensRaf = requestAnimationFrame(tick)
      }
    }
    lensRaf = requestAnimationFrame(tick)
  }

  const measure = (travelFx: boolean) => {
    const el = triggers.get(active())
    if (!el) {
      return
    }
    const x = el.offsetLeft
    const y = el.offsetTop
    const w = el.offsetWidth
    const h = el.offsetHeight
    const along = axis()
    if (along !== lastAxis) {
      lastAxis = along
      lastOffset = null
    }
    const offset = along === 'y' ? y : x
    if (travelFx && glide() && !reduced && lastOffset != null && offset !== lastOffset) {
      setDirection(offset > lastOffset ? 'right' : 'left')
      setMoving(false)
      requestAnimationFrame(() => setMoving(true))
      runLens()
      clearTimeout(settleTimer)
      settleTimer = setTimeout(() => {
        setDirection(null)
        setMoving(false)
        lensFrame(activeCenter())
      }, TRAVEL_MS + 60)
    }
    lastOffset = offset
    setPill({ x, y, w, h })
  }

  const revealActiveTab = () => {
    const el = triggers.get(active())
    if (!el || !listEl || axis() === 'y') {
      return
    }
    const elements = [...triggers.values()]
    const target = revealScrollLeft(
      elements.map((tab) => ({ left: tab.offsetLeft, width: tab.offsetWidth })),
      elements.indexOf(el),
      {
        scrollLeft: listEl.scrollLeft,
        clientWidth: listEl.clientWidth,
        scrollWidth: listEl.scrollWidth,
        inset: Number.parseFloat(getComputedStyle(listEl).scrollPaddingInlineStart) || 0,
      }
    )
    if (target !== listEl.scrollLeft) {
      listEl.scrollTo({ left: target, behavior: glide() && !reduced ? 'smooth' : 'auto' })
    }
  }

  createEffect(() => {
    active()
    axis()
    measure(true)
    revealActiveTab()
  })

  const remeasure = () => {
    measure(false)
    lensFrame(activeCenter())
    revealActiveTab()
  }

  onMount(() => {
    measure(false)
    setTimeout(() => {
      setGlide(true)
      lensFrame(activeCenter())
    }, 0)
    document.fonts?.ready.then(() => remeasure())
    const attachmentObserver = new MutationObserver((records) => {
      const list = listEl
      if (
        list?.isConnected &&
        records.some((record) => [...record.addedNodes].some((node) => node.contains(list)))
      ) {
        remeasure()
      }
    })
    attachmentObserver.observe(document.body, { childList: true, subtree: true })
    onCleanup(() => attachmentObserver.disconnect())
    if (listEl && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => remeasure())
      observer.observe(listEl)
      for (const trigger of triggers.values()) {
        observer.observe(trigger)
      }
      resizeObserver = observer
      onCleanup(() => {
        observer.disconnect()
        resizeObserver = undefined
      })
    }
    onCleanup(() => {
      clearTimeout(settleTimer)
      cancelAnimationFrame(lensRaf)
    })
  })

  return {
    setListEl: (el: HTMLElement) => {
      listEl = el
    },
    setIndicatorEl: (el: HTMLElement) => {
      indicatorEl = el
    },
    registerTrigger: (value: string, el: HTMLElement) => {
      triggers.set(value, el)
      resizeObserver?.observe(el)
    },
    pill,
    glide,
    direction,
    moving,
  }
}
